import {Checkbox, FormControl} from 'platform/components';
import {Show, Text, VStack} from 'platform/foundation';

import {useState} from 'react';
import {useController} from 'react-hook-form';

import {append, includes, isNil, isNotNil, omit} from 'ramda';

import {
  VehicleFinancingBalloonPayment,
  VehicleFinancingDownPayment,
  VehicleFinancingTerm,
} from '@dms/api/sales';
import i18n from '@dms/i18n';

import {Nullish, RequiredTestIdProps, suffixTestId} from 'shared/web';

import {VehicleFinancingTemplateFormValues} from '../VehicleFinancingTemplateForm/vehicleFinancingTemplateFormSchema';
import {RATE_MATRIX_TERMS} from './constants/RATE_MATRIX_TERMS';
import {RateMatrixBalloonBrackets} from './RateMatrixBalloonBrackets/RateMatrixBalloonBrackets';
import {RateMatrixBulkActions} from './RateMatrixBulkActions/RateMatrixBulkActions';
import {openRateMatrixCellDialog} from './RateMatrixCellForm/openRateMatrixCellDialog';
import {RateMatrixGrid} from './RateMatrixGrid/RateMatrixGrid';
import {RateMatrixTermPills} from './RateMatrixTermPills/RateMatrixTermPills';
import {RateMatrixCellData} from './types/RateMatrixCellData';
import {toggleRateMatrixCellSelection} from './utils/toggleRateMatrixCellSelection';

const RATE_MATRIX_FIELD_NAME = 'rateMatrix';

interface RateMatrixProps extends RequiredTestIdProps {
  control: FormControl<VehicleFinancingTemplateFormValues>;
  isBalloonType: boolean;
  // The matrix is a custom control, so the form-level disabled state has to be forwarded manually.
  isReadOnly: boolean;
  /**
   * Enables Shift-click multi-selection and the bulk-rate actions above the grid. Off by default
   * so the settings pages keep the plain one-cell-at-a-time editor.
   */
  isBulkEditEnabled?: boolean;
}

export function RateMatrix(props: RateMatrixProps) {
  const {field, fieldState} = useController<
    VehicleFinancingTemplateFormValues,
    typeof RATE_MATRIX_FIELD_NAME
  >({
    control: props.control,
    name: RATE_MATRIX_FIELD_NAME,
  });
  const value = field.value;
  const [browsedBracket, setBrowsedBracket] = useState<VehicleFinancingBalloonPayment | Nullish>(
    null
  );
  const [selectedCellKeys, setSelectedCellKeys] = useState<string[]>([]);

  const fallbackBracket = value.defaultBalloonPayment ?? value.availableBalloonBrackets[0] ?? null;
  const selectedBracket =
    isNotNil(browsedBracket) && includes(browsedBracket, value.availableBalloonBrackets)
      ? browsedBracket
      : fallbackBracket;

  const handleToggleTerm = (term: VehicleFinancingTerm) => {
    if (props.isReadOnly) {
      return;
    }

    const isEnabled = includes(term, value.availableTerms);
    const availableTerms = RATE_MATRIX_TERMS.filter((catalogueTerm) =>
      catalogueTerm === term ? !isEnabled : includes(catalogueTerm, value.availableTerms)
    );
    setSelectedCellKeys([]);
    field.onChange({...value, availableTerms});
  };

  const handleSelectBracket = (bracket: VehicleFinancingBalloonPayment) => {
    if (props.isReadOnly) {
      return;
    }

    setBrowsedBracket(bracket);
    setSelectedCellKeys([]);
    const isConfigured = includes(bracket, value.availableBalloonBrackets);
    if (isConfigured && isNotNil(value.defaultBalloonPayment)) {
      return;
    }

    field.onChange({
      ...value,
      availableBalloonBrackets: isConfigured
        ? value.availableBalloonBrackets
        : append(bracket, value.availableBalloonBrackets),
      defaultBalloonPayment: value.defaultBalloonPayment ?? bracket,
    });
  };

  const handleSetSelectedAsDefault = (isChecked: boolean) => {
    if (props.isReadOnly || isNil(selectedBracket) || !isChecked) {
      return;
    }

    const availableBalloonBrackets = includes(selectedBracket, value.availableBalloonBrackets)
      ? value.availableBalloonBrackets
      : append(selectedBracket, value.availableBalloonBrackets);
    field.onChange({...value, availableBalloonBrackets, defaultBalloonPayment: selectedBracket});
  };

  const openCellDialog = (
    term: VehicleFinancingTerm,
    downPayment: VehicleFinancingDownPayment,
    key: string
  ) => {
    const existingData = value.cells[key];

    openRateMatrixCellDialog({
      term,
      downPayment,
      balloonBracket: props.isBalloonType ? selectedBracket : null,
      initialData: existingData,
      onSubmit: (data: RateMatrixCellData) =>
        field.onChange({...value, cells: {...value.cells, [key]: data}}),
      onRemove: isNotNil(existingData)
        ? () => field.onChange({...value, cells: omit([key], value.cells)})
        : undefined,
      testId: suffixTestId(`cell-[${term}-${downPayment}]`, props),
    });
  };

  const handleCellActivate = (
    coordinates: {
      term: VehicleFinancingTerm;
      downPayment: VehicleFinancingDownPayment;
      cellKey: string;
    },
    isMultiSelect: boolean
  ) => {
    if (props.isReadOnly) {
      return;
    }

    if (props.isBulkEditEnabled === true && isMultiSelect) {
      setSelectedCellKeys(toggleRateMatrixCellSelection(selectedCellKeys, coordinates.cellKey));

      return;
    }

    setSelectedCellKeys([]);
    openCellDialog(coordinates.term, coordinates.downPayment, coordinates.cellKey);
  };

  const isMissingBracket = props.isBalloonType && isNil(selectedBracket);
  const hasEnabledTerms = value.availableTerms.length > 0;

  return (
    <VStack spacing={4} data-testid={suffixTestId('rateMatrix', props)}>
      <VStack spacing={2}>
        <Text size="small" color="secondary">
          {i18n.t('entity.vehicleFinancingTemplate.labels.availableTerms')}
        </Text>
        <RateMatrixTermPills
          availableTerms={value.availableTerms}
          isReadOnly={props.isReadOnly}
          onToggleTerm={handleToggleTerm}
          data-testid={suffixTestId('terms', props)}
        />
      </VStack>

      <Show when={props.isBalloonType}>
        <VStack spacing={2}>
          <Text size="small" color="secondary">
            {i18n.t('entity.vehicleFinancingTemplate.labels.balloonPayment')}
          </Text>
          <RateMatrixBalloonBrackets
            availableBalloonBrackets={value.availableBalloonBrackets}
            selectedBracket={selectedBracket}
            defaultBalloonPayment={value.defaultBalloonPayment}
            isReadOnly={props.isReadOnly}
            onSelect={handleSelectBracket}
            data-testid={suffixTestId('brackets', props)}
          />
          <Show when={isNotNil(selectedBracket)}>
            <Checkbox
              value={selectedBracket === value.defaultBalloonPayment}
              isDisabled={props.isReadOnly || selectedBracket === value.defaultBalloonPayment}
              label={i18n.t('entity.vehicleFinancingTemplate.actions.setDefaultBalloonPayment', {
                balloonPayment: selectedBracket,
              })}
              onChange={handleSetSelectedAsDefault}
              data-testid={suffixTestId('setDefaultPayment', props)}
            />
          </Show>
        </VStack>
      </Show>

      <Show when={fieldState.error?.message}>
        <Text size="small" color="danger">
          {fieldState.error?.message}
        </Text>
      </Show>
      <Show when={isMissingBracket}>
        <Text size="small" color="tertiary">
          {i18n.t('entity.vehicleFinancingTemplate.labels.selectBalloonBracket')}
        </Text>
      </Show>
      <Show when={!isMissingBracket && !hasEnabledTerms}>
        <Text size="small" color="tertiary">
          {i18n.t('entity.vehicleFinancingTemplate.labels.selectTerms')}
        </Text>
      </Show>
      <Show when={!isMissingBracket && hasEnabledTerms}>
        <VStack spacing={2}>
          <Show when={props.isBulkEditEnabled === true && !props.isReadOnly}>
            <Text size="xSmall" color="secondary">
              {i18n.t('page.vehicle.financingAndLeasing.multiSelectHint')}
            </Text>
            <Show when={selectedCellKeys.length > 0}>
              <RateMatrixBulkActions
                selectedCellKeys={selectedCellKeys}
                onClear={() => setSelectedCellKeys([])}
                onApply={(rates) => {
                  const appliedCells = Object.fromEntries(
                    selectedCellKeys.map((key) => [key, rates])
                  );
                  field.onChange({...value, cells: {...value.cells, ...appliedCells}});
                  setSelectedCellKeys([]);
                }}
                onRemove={() => {
                  field.onChange({...value, cells: omit(selectedCellKeys, value.cells)});
                  setSelectedCellKeys([]);
                }}
                data-testid={suffixTestId('bulk', props)}
              />
            </Show>
          </Show>
          <RateMatrixGrid
            availableTerms={value.availableTerms}
            cells={value.cells}
            balloonBracket={props.isBalloonType ? selectedBracket : null}
            isReadOnly={props.isReadOnly}
            selectedCellKeys={selectedCellKeys}
            onCellActivate={handleCellActivate}
            data-testid={suffixTestId('grid', props)}
          />
        </VStack>
      </Show>
    </VStack>
  );
}
